import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import SessionStatusPill from 'benchling-apps/interactivity/sessions/SessionStatusPill';
import {BenchlingAppSessionNode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {FONT_PROXIMA_16, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {BORDER_RADIUS_DEFAULT} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Badge, {BadgeColor} from 'ui-library/Badge';
import {GRAY, GRAY_LIGHTER, GRAY_DARK, RED_LIGHT} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import GenericCanvasActions from './GenericCanvasActions';

interface GenericCanvasShellProps {
  appName: string;
  featureName: string;
  children: React.ReactNode;
  canvasSession?: BenchlingAppSessionNode | null | undefined;
  canvasId?: string;
  dataTestAttrs?: DataTestAttrs;
  isInaccessible?: boolean;
  isFeatureMissing?: boolean;
}

export default function GenericCanvasShell(props: GenericCanvasShellProps): JSX.Element {
  return (
    <div className={css(styles.canvas)} {...getTestProps(props.dataTestAttrs)}>
      <div className={css(styles.canvasHeader)}>
        <div className={css(styles.canvasInfo)}>
          <div className={css(styles.iconContainer)}>
            <BenchlingIcon icon="plug" className={css(styles.icon)} />
          </div>
          <div className={css(styles.featureDetailsContainer)}>
            <div className={css(styles.featureDetails)}>
              <p
                className={css(
                  styles.featureName,
                  (props.isFeatureMissing || props.isInaccessible) && styles.warning
                )}
                {...getTestProps({
                  component: 'GenericCanvasShell',
                  element: 'featureName',
                })}
              >
                {props.featureName}
              </p>
              <p
                {...getTestProps({
                  component: 'GenericCanvasShell',
                  element: 'appName',
                })}
                className={css(props.isInaccessible && styles.warning)}
              >
                {props.appName}
              </p>
            </div>
            <Badge color={BadgeColor.GRAY} className="u-tiny-spacing-left">
              App
            </Badge>
          </div>
        </div>
        <div className={css(styles.canvasInfo)}>
          {props.canvasSession && <SessionStatusPill status={props.canvasSession.status} />}
          <GenericCanvasActions canvasId={props.canvasId} />
        </div>
      </div>
      <div className={css(styles.blockContainer)}>{props.children}</div>
    </div>
  );
}

const styles = StyleSheet.create({
  canvas: {
    padding: '16px',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: GRAY,
    backgroundColor: 'white',
  },
  warning: {
    color: RED_LIGHT,
  },
  canvasHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  blockContainer: {
    marginTop: '20px',
    ':first-child': {
      marginTop: 0,
    },
  },
  canvasInfo: {
    display: 'flex',
  },
  featureName: {
    ...FONT_PROXIMA_16,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    margin: '0',
  },
  iconContainer: {
    borderStyle: 'solid',
    borderColor: GRAY,
    backgroundColor: GRAY_LIGHTER,
    // try to make the icon container a square
    padding: '0px 15px',
    borderRadius: BORDER_RADIUS_DEFAULT,
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    fontSize: '200%',
    color: GRAY_DARK,
    transform: 'rotate(45deg)',
  },
  featureDetailsContainer: {
    display: 'flex',
    alignItems: 'center',
    lineHeight: '1',
    margin: '0',
  },
  featureDetails: {
    marginLeft: '12px',
    marginRight: '10px',
    marginBottom: '0',
  },
});
